import React from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  colors,
  layerZIndex,
  numbers,
  typography,
  shadows,
} from "../../styles/designTokens";

const globalUploadTooltipSlotProps = {
  popper: {
    sx: { zIndex: layerZIndex.globalUploadTooltip },
  },
};

const PANEL_BG = "#2A2A2D";
const PROGRESS_TRACK = "rgba(255, 255, 255, 0.10)";

interface DownloadToastProps {
  open?: boolean;
  title: string;
  fileName?: string;
  statusText?: string;
  progress?: number;
  onCancel?: () => void;
  onClose?: () => void;
  minimized?: boolean;
  onToggleMinimize?: () => void;
}

const HeaderChevronIcon: React.FC<{ minimized: boolean }> = ({ minimized }) => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 24, height: 24, display: "block" }} aria-hidden>
    <path
      d={minimized ? "M7 14L12 9L17 14" : "M7 10L12 15L17 10"}
      fill="none"
      stroke="#BDBDBF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const HeaderCloseIcon: React.FC = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 24, height: 24, display: "block" }} aria-hidden>
    <path
      d="M7 7L17 17M17 7L7 17"
      fill="none"
      stroke="#BDBDBF"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Box>
);

const CancelCircleIcon: React.FC = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 24, height: 24, display: "block" }} aria-hidden>
    <circle cx="12" cy="12" r="10" fill="none" stroke="#FF4242" strokeWidth="2" />
    <path
      d="M9 9L15 15M15 9L9 15"
      fill="none"
      stroke="#FF4242"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Box>
);

const CompleteCircleIcon: React.FC = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 24, height: 24, display: "block" }} aria-hidden>
    <circle cx="12" cy="12" r="10" fill="none" stroke="#50B786" strokeWidth="2" />
    <path
      d="M8 12.5L10.8 15L16 9.5"
      fill="none"
      stroke="#50B786"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const DownloadToast: React.FC<DownloadToastProps> = ({
  open = true,
  title,
  fileName,
  statusText,
  progress = 0,
  onCancel,
  onClose,
  minimized = false,
  onToggleMinimize,
}) => {
  if (!open) return null;

  const loweredStatus = (statusText || "").toLowerCase();
  const isCompleted = loweredStatus.includes("complete");
  const isFailed = loweredStatus.includes("failed") || loweredStatus.includes("cancelled");

  return (
    <Box
      sx={{
        width: 331,
        height: minimized ? 48 : 130,
        background: PANEL_BG,
        color: colors.common[100].dark,
        borderTopLeftRadius: numbers.cornerRadius[12],
        borderTopRightRadius: numbers.cornerRadius[12],
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        boxShadow: shadows.fileUploadToast,
        overflow: "hidden",
        pointerEvents: "auto",
      }}
    >
      <Box
        sx={{
          height: 48,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: numbers.gap[24],
          pr: numbers.gap[16],
          py: numbers.gap[12],
          borderBottom: minimized ? "none" : `1px solid ${colors.grey[40].dark}`,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: colors.grey[80].dark, ...typography.styles.caption }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          <Tooltip
            title={minimized ? "Expand" : "Minimize"}
            slotProps={globalUploadTooltipSlotProps}
          >
            <IconButton size="small" onClick={onToggleMinimize} sx={headerIconBtnSx} disableRipple>
              <HeaderChevronIcon minimized={minimized} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close" slotProps={globalUploadTooltipSlotProps}>
            <IconButton size="small" onClick={onClose} sx={headerIconBtnSx} disableRipple>
              <HeaderCloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {!minimized && (
        <Box
          sx={{
            height: 82,
            px: numbers.gap[24],
            py: numbers.gap[16],
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: numbers.gap[12] }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ ...typography.styles.label2Medium, color: colors.common[100].dark, mb: "2px" }} noWrap>
                {fileName || "File name.viewr"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  ...typography.styles.caption,
                  color: isFailed ? colors.red[50].dark : colors.grey[80].dark,
                  lineHeight: "14px",
                }}
              >
                {statusText}
              </Typography>
            </Box>
            {isCompleted ? (
              <CompleteCircleIcon />
            ) : (
              <Tooltip title="Cancel" slotProps={globalUploadTooltipSlotProps}>
                <IconButton size="small" onClick={onCancel} sx={cancelIconBtnSx} disableRipple>
                  <CancelCircleIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, progress))}
            sx={{
              height: 8,
              borderRadius: numbers.cornerRadius[40],
              backgroundColor: PROGRESS_TRACK,
              "& .MuiLinearProgress-bar": {
                backgroundColor: isFailed ? colors.red[50].dark : colors.primary[50].dark,
                borderRadius: numbers.cornerRadius[40],
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const headerIconBtnSx = {
  color: colors.grey[80].dark,
  padding: 0,
  width: "24px",
  height: "24px",
  minWidth: "24px",
  minHeight: "24px",
  borderRadius: "999px",
  "&:hover": {
    backgroundColor: "transparent",
  },
};

const cancelIconBtnSx = {
  padding: 0,
  minWidth: "24px",
  minHeight: "24px",
  width: "24px",
  height: "24px",
  color: colors.red[50].dark,
  backgroundColor: "transparent",
  borderRadius: "50%",
  border: "none",
  boxSizing: "border-box",
  "&:hover": {
    backgroundColor: "transparent",
  },
};

export default DownloadToast;
